import React, { useState, useEffect } from 'react';
import { getSupportStats } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import {
  FiBarChart2,
  FiTrendingUp,
  FiTrendingDown,
  FiClock,
  FiUsers,
  FiMessageSquare,
  FiAlertCircle,
  FiCheckCircle,
  FiRefreshCw,
  FiCalendar,
  FiFilter,
  FiDownload,
  FiStar,
  FiAward,
  FiActivity,
  FiShield,
  FiPackage,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiChevronRight
} from 'react-icons/fi';

const SupportStats = ({ admin }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date()
  });
  const [viewMode, setViewMode] = useState('overview'); // overview, detailed, trends
  const [expandedSection, setExpandedSection] = useState('summary');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await getSupportStats();
      console.log('Support stats response:', response);
      
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setStats(response);
      }
    } catch (err) {
      Alert.error('Failed to fetch statistics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (hours) => {
    if (!hours) return 'N/A';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const getTrendIndicator = (current, previous) => {
    if (!previous) return { icon: null, color: 'text-gray-400', value: '0%' };
    const trend = ((current - previous) / previous) * 100;
    if (trend > 0) {
      return { 
        icon: <FiTrendingUp className="inline" />, 
        color: 'text-green-400',
        value: `+${trend.toFixed(1)}%`
      };
    } else if (trend < 0) {
      return { 
        icon: <FiTrendingDown className="inline" />, 
        color: 'text-red-400',
        value: `${trend.toFixed(1)}%`
      };
    }
    return { icon: null, color: 'text-gray-400', value: '0%' };
  };

  // Loading Skeleton
  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statsData = stats?.stats || {};
  const categories = stats?.categories || [];
  const admins = stats?.admins || [];
  const recentTickets = stats?.recent_tickets || [];

  const summaryTrend = getTrendIndicator(
    statsData.tickets_last_7_days || 0,
    statsData.tickets_previous_7_days || 0
  );

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Support</span> Statistics
            </h1>
            <p className="text-gray-400 mt-1">Performance metrics and analytics dashboard</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={fetchStats}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center">
              <FiDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-2 mb-6">
        <div className="flex space-x-1">
          {[
            { id: 'overview', label: 'Overview', icon: <FiBarChart2 /> },
            { id: 'detailed', label: 'Detailed', icon: <FiGrid /> },
            { id: 'trends', label: 'Trends', icon: <FiTrendingUp /> }
          ].map((view) => (
            <button
              key={view.id}
              onClick={() => setViewMode(view.id)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                viewMode === view.id
                  ? 'bg-[#ff660e] text-white shadow-lg'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
              }`}
            >
              {view.icon}
              <span className="ml-2">{view.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Tickets */}
        <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-xl border border-blue-700/30 p-5 hover:border-blue-600/50 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2.5 bg-blue-900/40 rounded-lg group-hover:scale-110 transition-transform">
              <FiMessageSquare className="text-blue-400 text-xl" />
            </div>
            <span className="text-xs text-blue-300 bg-blue-900/30 px-2 py-1 rounded">
              All time
            </span>
          </div>
          <div className="text-2xl font-bold text-white group-hover:text-blue-300 transition-colors">
            {formatNumber(statsData.total_tickets)}
          </div>
          <div className="text-sm text-gray-400 mt-1">Total Tickets</div>
          <div className="flex items-center text-xs text-gray-500 mt-2">
            <span className="flex-1">{statsData.tickets_last_7_days || 0} last 7 days</span>
            <span className={`${summaryTrend.color}`}>
              {summaryTrend.value}
              {summaryTrend.icon}
            </span>
          </div>
        </div>

        {/* Resolved Tickets */}
        <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl border border-green-700/30 p-5 hover:border-green-600/50 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2.5 bg-green-900/40 rounded-lg group-hover:scale-110 transition-transform">
              <FiCheckCircle className="text-green-400 text-xl" />
            </div>
            <span className="text-xs text-green-300 bg-green-900/30 px-2 py-1 rounded">
              {Math.round((statsData.resolved_tickets || 0) / (statsData.total_tickets || 1) * 100)}% rate
            </span>
          </div>
          <div className="text-2xl font-bold text-white group-hover:text-green-300 transition-colors">
            {formatNumber(statsData.resolved_tickets)}
          </div>
          <div className="text-sm text-gray-400 mt-1">Resolved</div>
          <div className="text-xs text-gray-500 mt-2">
            {statsData.closed_tickets || 0} closed
          </div>
        </div>

        {/* Average Response */}
        <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 rounded-xl border border-yellow-700/30 p-5 hover:border-yellow-600/50 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2.5 bg-yellow-900/40 rounded-lg group-hover:scale-110 transition-transform">
              <FiClock className="text-yellow-400 text-xl" />
            </div>
            <span className="text-xs text-yellow-300 bg-yellow-900/30 px-2 py-1 rounded">
              Avg. time
            </span>
          </div>
          <div className="text-2xl font-bold text-white group-hover:text-yellow-300 transition-colors">
            {formatTime(statsData.avg_response_hours)}
          </div>
          <div className="text-sm text-gray-400 mt-1">Avg. Response</div>
          <div className="text-xs text-gray-500 mt-2">
            {formatTime(statsData.avg_resolution_hours)} resolution
          </div>
        </div>

        {/* Unassigned */}
        <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-xl border border-purple-700/30 p-5 hover:border-purple-600/50 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2.5 bg-purple-900/40 rounded-lg group-hover:scale-110 transition-transform">
              <FiUsers className="text-purple-400 text-xl" />
            </div>
            <span className="text-xs text-purple-300 bg-purple-900/30 px-2 py-1 rounded">
              Needs assignment
            </span>
          </div>
          <div className="text-2xl font-bold text-white group-hover:text-purple-300 transition-colors">
            {formatNumber(statsData.unassigned_tickets)}
          </div>
          <div className="text-sm text-gray-400 mt-1">Unassigned</div>
          <div className="text-xs text-gray-500 mt-2">
            {admins.length || 0} active admins
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 hover:border-blue-500/50 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Open Tickets</p>
              <p className="text-xl font-bold text-blue-400 mt-1">
                {formatNumber(statsData.open_tickets)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
              <FiAlertCircle className="text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 hover:border-yellow-500/50 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">In Progress</p>
              <p className="text-xl font-bold text-yellow-400 mt-1">
                {formatNumber(statsData.in_progress_tickets)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-yellow-900/30 flex items-center justify-center">
              <FiActivity className="text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 hover:border-orange-500/50 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Awaiting User</p>
              <p className="text-xl font-bold text-orange-400 mt-1">
                {formatNumber(statsData.awaiting_user_tickets)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-orange-900/30 flex items-center justify-center">
              <FiClock className="text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 hover:border-red-500/50 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Urgent Tickets</p>
              <p className="text-xl font-bold text-red-400 mt-1">
                {formatNumber(statsData.urgent_tickets)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-red-900/30 flex items-center justify-center">
              <FiAlertCircle className="text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Categories Breakdown */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FiGrid className="text-[#ff660e] mr-2" />
                <h3 className="text-lg font-medium text-white">Tickets by Category</h3>
              </div>
              <span className="text-sm text-gray-400">
                {categories.length} categories
              </span>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {categories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No category data available</p>
                </div>
              ) : (
                categories.map((category, index) => {
                  const percentage = Math.min(100, (category.ticket_count || 0) / (statsData.total_tickets || 1) * 100);
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-xl">{category.category_icon || '📁'}</span>
                          <span className="text-sm text-gray-300">{category.category_name}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-white">
                            {formatNumber(category.ticket_count || 0)}
                          </span>
                          <span className="text-xs text-gray-400">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className={`h-full rounded-full ${
                            category.ticket_count > 20 ? 'bg-gradient-to-r from-red-500 to-pink-500' :
                            category.ticket_count > 10 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                            'bg-gradient-to-r from-blue-500 to-cyan-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Admin Performance */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FiAward className="text-[#ff660e] mr-2" />
                <h3 className="text-lg font-medium text-white">Top Admins</h3>
              </div>
              <span className="text-sm text-gray-400">
                {admins.length} admins
              </span>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {admins.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No admin data available</p>
                </div>
              ) : (
                admins.slice(0, 5).map((agent, index) => (
                  <div key={index} className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 hover:border-gray-500 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-900/50 to-purple-900/50 flex items-center justify-center">
                          <span className="text-sm font-bold text-white">
                            {agent.admin_name?.[0] || 'A'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-white truncate">
                            {agent.admin_name || 'Admin'}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{agent.admin_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">
                          {formatNumber(agent.assigned_tickets || 0)}
                        </div>
                        <div className="text-xs text-gray-400">assigned</div>
                        {agent.avg_resolution_time && (
                          <div className="text-xs text-blue-400 mt-1">
                            {formatTime(agent.avg_resolution_time)} avg
                          </div>
                        )}
                      </div>
                    </div>
                    {agent.resolved_tickets && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-600">
                        <span className="text-xs text-gray-400">
                          {agent.resolved_tickets} resolved
                        </span>
                        <span className="text-xs text-green-400">
                          {Math.round((agent.resolved_tickets || 0) / (agent.assigned_tickets || 1) * 100)}% success
                        </span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Tickets */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-white">Recent Tickets</h3>
              <p className="text-sm text-gray-400">Latest support requests</p>
            </div>
            <button className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center">
              View All
              <FiChevronRight className="ml-1" />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {recentTickets.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No recent tickets
                  </td>
                </tr>
              ) : (
                recentTickets.map((ticket, index) => (
                  <tr key={index} className="hover:bg-gray-750 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-white">{ticket.title}</p>
                        <p className="text-sm text-gray-500">#{ticket.ticket_id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-300">
                            {ticket.first_name?.[0]}{ticket.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {ticket.first_name} {ticket.last_name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        ticket.status === 'open' 
                          ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50' :
                          ticket.status === 'in_progress' 
                          ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/50' :
                          ticket.status === 'resolved' 
                          ? 'bg-green-900/30 text-green-400 border border-green-800/50' :
                          'bg-gray-700 text-gray-400 border border-gray-600'
                      }`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        ticket.priority === 'urgent' 
                          ? 'bg-red-900/30 text-red-400 border border-red-800/50' :
                          ticket.priority === 'high' 
                          ? 'bg-orange-900/30 text-orange-400 border border-orange-800/50' :
                          ticket.priority === 'medium' 
                          ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/50' :
                          'bg-blue-900/30 text-blue-400 border border-blue-800/50'
                      }`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {ticket.formatted_date || new Date(ticket.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FiBarChart2 className="text-[#ff660e] mr-2" />
            <h3 className="text-lg font-medium text-white">Additional Metrics</h3>
          </div>
          <button
            onClick={() => setExpandedSection(expandedSection === 'metrics' ? null : 'metrics')}
            className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center"
          >
            {expandedSection === 'metrics' ? 'Hide Details' : 'Show Details'}
            {expandedSection === 'metrics' ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />}
          </button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
            <p className="text-sm text-gray-400">Last 7 Days</p>
            <p className="text-xl font-bold text-white mt-1">
              {formatNumber(statsData.tickets_last_7_days || 0)}
            </p>
            <p className="text-xs text-gray-500">tickets created</p>
          </div>
          <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
            <p className="text-sm text-gray-400">Last 24h</p>
            <p className="text-xl font-bold text-white mt-1">
              {formatNumber(statsData.tickets_last_24h || 0)}
            </p>
            <p className="text-xs text-gray-500">tickets created</p>
          </div>
          <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
            <p className="text-sm text-gray-400">Avg. Resolution</p>
            <p className="text-xl font-bold text-white mt-1">
              {formatTime(statsData.avg_resolution_hours)}
            </p>
            <p className="text-xs text-gray-500">per ticket</p>
          </div>
          <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
            <p className="text-sm text-gray-400">High Priority</p>
            <p className="text-xl font-bold text-white mt-1">
              {formatNumber(statsData.high_tickets || 0)}
            </p>
            <p className="text-xs text-gray-500">tickets</p>
          </div>
        </div>

        {expandedSection === 'metrics' && (
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
                <p className="text-sm text-gray-400">First Response</p>
                <p className="text-lg font-bold text-green-400 mt-1">
                  {formatTime(statsData.avg_first_response_hours)}
                </p>
              </div>
              <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
                <p className="text-sm text-gray-400">Satisfaction</p>
                <p className="text-lg font-bold text-purple-400 mt-1">
                  {statsData.satisfaction_rate || 'N/A'}%
                </p>
              </div>
              <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
                <p className="text-sm text-gray-400">Reopened</p>
                <p className="text-lg font-bold text-orange-400 mt-1">
                  {formatNumber(statsData.reopened_tickets || 0)}
                </p>
              </div>
              <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 text-center">
                <p className="text-sm text-gray-400">Escalated</p>
                <p className="text-lg font-bold text-red-400 mt-1">
                  {formatNumber(statsData.escalated_tickets || 0)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Performance Trends Placeholder */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FiTrendingUp className="text-[#ff660e] mr-2" />
            <h3 className="text-lg font-medium text-white">Performance Trends</h3>
          </div>
          <span className="text-sm text-gray-400">Last 30 days</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-900 rounded-lg p-6 h-64 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-600 text-4xl mb-3">📈</div>
              <p className="text-gray-400">Ticket Volume Over Time</p>
              <p className="text-sm text-gray-500 mt-1">Daily/weekly ticket counts</p>
            </div>
          </div>
          <div className="bg-gray-900 rounded-lg p-6 h-64 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-600 text-4xl mb-3">⏱️</div>
              <p className="text-gray-400">Resolution Time Trends</p>
              <p className="text-sm text-gray-500 mt-1">Average resolution times</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportStats;